// Waste data layer (Module 05 §5). TanStack Query hooks over the generated
// api-client — doctrine 3: apps/web talks to the API only through
// `@assessly/api-client`, never a hand-rolled fetch. Every mutation sends a
// fresh Idempotency-Key (§5: "mutations require Idempotency-Key") and
// invalidates the lists it affects.
//
// The catalogs are NOT paginated (a bounded catalog the floor holds whole), so
// there is no cursor plumbing here — a page-walk would be inventing a control
// the server does not offer.

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { components, paths } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { newIdempotencyKey, unwrap } from "../../../../lib/request";

type S = components["schemas"];

export type WasteCategory = S["WasteCategory"];
export type WasteCauseCode = S["WasteCauseCode"];
export type RemnantPriceClass = S["RemnantPriceClass"];
export type WasteSettings = S["WasteSettingsResponse"];
export type WasteCategoryUpdate = S["WasteCategoryUpdateRequest"];
export type WasteCauseUpdate = S["WasteCauseUpdateRequest"];
export type RemnantPriceClassUpdate = S["RemnantPriceClassUpdateRequest"];
export type WasteSettingsUpdate = S["WasteSettingsUpdateRequest"];
export type WasteCategoryCreate = S["WasteCategoryCreateRequest"];
export type WasteCauseCreate = S["WasteCauseCreateRequest"];
export type RemnantPriceClassCreate = S["RemnantPriceClassCreateRequest"];

export const wasteKeys = {
  all: ["waste"] as const,
  catalog: () => ["waste", "catalog"] as const,
  priceClasses: () => ["waste", "price-classes"] as const,
  settings: () => ["waste", "settings"] as const,
  // 05-B — the ledger, rollups and remnant registry.
  events: (query: WasteEventsQuery, cursor: string | undefined) =>
    ["waste", "events", query, cursor ?? null] as const,
  eventLists: () => ["waste", "events"] as const,
  rollups: (query: WasteRollupsQuery) => ["waste", "rollups", query] as const,
  rollupLists: () => ["waste", "rollups"] as const,
  remnants: (query: RemnantsQuery, cursor: string | undefined) =>
    ["waste", "remnants", query, cursor ?? null] as const,
  remnantLists: () => ["waste", "remnants"] as const,
};

// ---------------------------------------------------------------------------
// 05-B — S2 ledger, S4 rollups, S5 remnant registry
// ---------------------------------------------------------------------------

export type WasteEvent = S["WasteEvent"];
export type Remnant = S["Remnant"];
export type WasteRollupRow = S["WasteRollupRow"];
export type WasteEventsQuery = NonNullable<paths["/v1/waste/events"]["get"]["parameters"]["query"]>;
export type WasteRollupsQuery = NonNullable<
  paths["/v1/waste/rollups"]["get"]["parameters"]["query"]
>;
export type RemnantsQuery = NonNullable<paths["/v1/waste/remnants"]["get"]["parameters"]["query"]>;

export function useWasteEvents(
  query: WasteEventsQuery,
  cursor: string | undefined,
  enabled = true,
) {
  return useQuery({
    queryKey: wasteKeys.events(query, cursor),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/events", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the waste ledger");
    },
  });
}

export function useWasteRollups(query: WasteRollupsQuery, enabled = true, refetchMs?: number) {
  return useQuery({
    queryKey: wasteKeys.rollups(query),
    enabled,
    ...(refetchMs !== undefined ? { refetchInterval: refetchMs } : {}),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/rollups", { signal, params: { query } });
      return unwrap(res, "Could not load the waste rollups").items;
    },
  });
}

export function useRemnants(query: RemnantsQuery, cursor: string | undefined, enabled = true) {
  return useQuery({
    queryKey: wasteKeys.remnants(query, cursor),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/remnants", {
        signal,
        params: { query: { ...query, ...(cursor !== undefined ? { cursor } : {}) } },
      });
      return unwrap(res, "Could not load the remnant registry");
    },
  });
}

export function useSetWasteDisposition() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: S["WasteEventDispositionRequest"] }) => {
      const res = await client.POST("/v1/waste/events/{id}/disposition", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not set the disposition");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wasteKeys.eventLists() });
      void qc.invalidateQueries({ queryKey: wasteKeys.rollupLists() });
    },
  });
}

export function useReverseWasteEvent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const res = await client.POST("/v1/waste/events/{id}/reverse", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason: input.reason },
      });
      return unwrap(res, "Could not reverse the event");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wasteKeys.eventLists() });
      void qc.invalidateQueries({ queryKey: wasteKeys.rollupLists() });
    },
  });
}

export function useReserveRemnant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: S["RemnantReserveRequest"] }) => {
      const res = await client.POST("/v1/waste/remnants/{id}/reserve", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not reserve the remnant");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.remnantLists() }),
  });
}

export function useReleaseRemnant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await client.POST("/v1/waste/remnants/{id}/release", {
        params: { path: { id }, header: { "Idempotency-Key": newIdempotencyKey() } },
      });
      return unwrap(res, "Could not release the reservation");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.remnantLists() }),
  });
}

export function useReclassRemnant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; priceClassId: string }) => {
      const res = await client.POST("/v1/waste/remnants/{id}/reclass", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { price_class_id: input.priceClassId },
      });
      return unwrap(res, "Could not re-class the remnant");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.remnantLists() }),
  });
}

export function useScrapRemnant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; reason: string }) => {
      const res = await client.POST("/v1/waste/remnants/{id}/scrap", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: { reason: input.reason },
      });
      return unwrap(res, "Could not scrap the remnant");
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: wasteKeys.remnantLists() });
      void qc.invalidateQueries({ queryKey: wasteKeys.eventLists() });
    },
  });
}

/** The category catalog WITH its causes — one round trip, because the editor
 * (and the floor grid it mirrors) needs both to mean anything. */
export function useWasteCatalog() {
  return useQuery({
    queryKey: wasteKeys.catalog(),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/categories", {
        signal,
        params: { query: { include: "causes" } },
      });
      const data = unwrap(res, "Could not load the waste catalog");
      return { categories: data.items, causes: data.causes ?? [] };
    },
  });
}

export function useRemnantPriceClasses() {
  return useQuery({
    queryKey: wasteKeys.priceClasses(),
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/price-classes", { signal });
      return unwrap(res, "Could not load the price classes").items;
    },
  });
}

export function useWasteSettings(enabled: boolean) {
  return useQuery({
    queryKey: wasteKeys.settings(),
    enabled,
    queryFn: async ({ signal }) => {
      const res = await client.GET("/v1/waste/settings", { signal });
      return unwrap(res, "Could not load the waste settings");
    },
  });
}

export function useUpdateWasteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: WasteCategoryUpdate }) => {
      const res = await client.PUT("/v1/waste/categories/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the category");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.catalog() }),
  });
}

export function useCreateWasteCategory() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WasteCategoryCreate) => {
      const res = await client.POST("/v1/waste/categories", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the category");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.catalog() }),
  });
}

export function useUpdateWasteCause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: WasteCauseUpdate }) => {
      const res = await client.PUT("/v1/waste/causes/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the cause");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.catalog() }),
  });
}

export function useCreateWasteCause() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WasteCauseCreate) => {
      const res = await client.POST("/v1/waste/causes", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the cause");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.catalog() }),
  });
}

export function useUpdatePriceClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; body: RemnantPriceClassUpdate }) => {
      const res = await client.PUT("/v1/waste/price-classes/{id}", {
        params: { path: { id: input.id }, header: { "Idempotency-Key": newIdempotencyKey() } },
        body: input.body,
      });
      return unwrap(res, "Could not save the price class");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.priceClasses() }),
  });
}

export function useCreatePriceClass() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: RemnantPriceClassCreate) => {
      const res = await client.POST("/v1/waste/price-classes", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not create the price class");
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: wasteKeys.priceClasses() }),
  });
}

export function useUpdateWasteSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: WasteSettingsUpdate) => {
      const res = await client.PUT("/v1/waste/settings", {
        params: { header: { "Idempotency-Key": newIdempotencyKey() } },
        body,
      });
      return unwrap(res, "Could not save the settings");
    },
    onSuccess: (data) => qc.setQueryData(wasteKeys.settings(), data),
  });
}
